/**
 * Legacy Route Redirects
 * Story PU-6.8: Marketing Navigation Integration
 *
 * Provides redirect components for legacy Marketing routes to the new
 * plugin-based routes. Uses Navigate with replace for clean history.
 *
 * Redirect Mapping:
 * - /marketing → /plugins/marketing/dashboard
 * - /marketing/chat → /plugins/marketing/chat
 * - /marketing/chat/:agentId → /plugins/marketing/chat?agent=:agentId
 * - /marketing/calendar → /plugins/marketing/calendar
 * - /content-calendar → /plugins/marketing/calendar
 * - /marketing/campaigns → /plugins/marketing/library
 * - /campaigns → /plugins/marketing/library
 * - /marketing/settings → /plugins/marketing/settings
 *
 * @see docs/tech-spec-epic-pu-6.md#AC-8.4
 */

import { Navigate, useParams, useSearchParams } from 'react-router-dom';

/**
 * Redirects /marketing/chat/:agentId to /plugins/marketing/chat?agent=:agentId
 *
 * Converts route parameter to query parameter for deep linking support.
 * Uses replace to ensure clean browser history without the legacy route.
 *
 * @example
 * // Route: /marketing/chat/foundation-builder
 * // Redirects to: /plugins/marketing/chat?agent=foundation-builder
 */
export function LegacyAgentChatRedirect() {
  const { agentId } = useParams<{ agentId: string }>();
  const [searchParams] = useSearchParams();

  // Preserve any existing query params
  const newParams = new URLSearchParams(searchParams);
  if (agentId) {
    newParams.set('agent', agentId);
  }

  const queryString = newParams.toString();
  const targetPath = `/plugins/marketing/chat${queryString ? `?${queryString}` : ''}`;

  return <Navigate to={targetPath} replace />;
}

/**
 * Redirects /campaigns/:id to /plugins/marketing/library?doc=:id
 *
 * Converts route parameter to query parameter for document deep linking.
 * Uses replace for clean history.
 *
 * @example
 * // Route: /campaigns/abc123
 * // Redirects to: /plugins/marketing/library?doc=abc123
 */
export function LegacyCampaignDetailRedirect() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();

  // Preserve existing query params
  const newParams = new URLSearchParams(searchParams);
  if (id) {
    newParams.set('doc', id);
  }

  const queryString = newParams.toString();
  const targetPath = `/plugins/marketing/library${queryString ? `?${queryString}` : ''}`;

  return <Navigate to={targetPath} replace />;
}

/**
 * Simple redirect components for routes without parameters
 */
// eslint-disable-next-line react-refresh/only-export-components -- route-element map consumed by App.tsx, not a component
export const LegacyMarketingRedirects = {
  /**
   * /marketing → /plugins/marketing/dashboard
   */
  MarketingRoot: () => <Navigate to="/plugins/marketing/dashboard" replace />,

  /**
   * /marketing/chat → /plugins/marketing/chat
   */
  MarketingChat: () => <Navigate to="/plugins/marketing/chat" replace />,

  /**
   * /marketing/calendar → /plugins/marketing/calendar
   */
  MarketingCalendar: () => <Navigate to="/plugins/marketing/calendar" replace />,

  /**
   * /content-calendar → /plugins/marketing/calendar
   */
  ContentCalendar: () => <Navigate to="/plugins/marketing/calendar" replace />,

  /**
   * /marketing/campaigns → /plugins/marketing/library
   */
  MarketingCampaigns: () => <Navigate to="/plugins/marketing/library" replace />,

  /**
   * /campaigns → /plugins/marketing/library
   */
  Campaigns: () => <Navigate to="/plugins/marketing/library" replace />,

  /**
   * /marketing/settings → /plugins/marketing/settings
   */
  MarketingSettings: () => <Navigate to="/plugins/marketing/settings" replace />,
};

export default LegacyMarketingRedirects;
